
import { Link } from 'react-router-dom';
import { resetAllTours, resetTour, TOUR_IDS, TourId } from '../hooks/useProductTour';
import React, { useMemo, useState } from 'react';
import './HelpCenterPage.css';

interface Section {
  id: string;
  icon: React.ReactNode;
  title: string;
  category: string;
  content: React.ReactNode;
}

// Consistent 20x20 line-icon set (matches the stroke-based icon language already
// used in the app's own sidebar nav) — replaces the previous emoji icons, which
// render inconsistently across operating systems and read as informal rather
// than a mature, polished product.
const icon = (paths: React.ReactNode) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);

const Icons = {
  shield: icon(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />),
  folder: icon(<path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />),
  check: icon(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></>),
  target: icon(<><circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" /></>),
  play: icon(<><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></>),
  layers: icon(<><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>),
  eyeCheck: icon(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>),
  bell: icon(<><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></>),
  sliders: icon(<><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>),
  lock: icon(<><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></>),
  search: icon(<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  star: icon(<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />),
  grid: icon(<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>),
  bug: icon(<><circle cx="12" cy="13" r="6" /><path d="M12 7V3M8 5l2 2M16 5l-2 2M6 13H2M22 13h-4M8 19l-2 2M16 19l2 2" /></>),
  zap: icon(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />),
  book: icon(<><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></>),
};

const sections: Section[] = [
  {
    id: 'overview',
    icon: Icons.shield,
    title: 'What is AssureX?',
    category: 'Getting Started',
    content: (
      <div>
        <p>
          <strong>AssureX</strong> is an enterprise test management platform designed to help QA teams
          organize, execute, and track software testing across projects. It provides a structured
          workspace for managing test cases, test scenarios, and test runs — all in one place.
        </p>
        <ul>
          <li>Organize test assets in a hierarchical folder structure</li>
          <li>Create and manage test cases and test scenarios independently</li>
          <li>Run tests and capture execution results with evidence</li>
          <li>Export test data and share folders with stakeholders</li>
          <li>Integrate with CI/CD pipelines via S3 XML snapshots</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'projects',
    icon: Icons.folder,
    title: 'Projects',
    category: 'Getting Started',
    content: (
      <div>
        <p>Projects are the top-level containers in AssureX. Each project represents a product, application, or testing initiative.</p>
        <h4>How to use Projects</h4>
        <ul>
          <li><strong>Create a Project</strong> — Click <em>Add New Project</em> on the Projects screen. Enter a name, optional description, and upload an icon.</li>
          <li><strong>Open a Project</strong> — Click any project card to navigate into its dashboard.</li>
          <li><strong>Edit a Project</strong> — Click the pencil icon on the project card to update its name, description, or icon.</li>
          <li><strong>Delete a Project</strong> — Click the trash icon. This permanently deletes the project and all its subfolders and test data.</li>
        </ul>
        <div className="help-tip">
          <span className="help-tip-icon">{Icons.bell}</span>
          <span>Projects are shared across your organization. All team members with access can view and edit them.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'test-cases',
    icon: Icons.check,
    title: 'Test Cases',
    category: 'Core Workflows',
    content: (
      <div>
        <p>Test Cases represent individual, step-by-step verification procedures. They are organized in a folder tree under the <strong>Test Cases</strong> section.</p>
        <h4>Folder Structure</h4>
        <ul>
          <li>Root folders (level 0) are project-level containers — they show statistics only.</li>
          <li>Subfolders hold the actual test cases and can be nested to any depth.</li>
        </ul>
        <h4>Creating Test Cases</h4>
        <ul>
          <li>Select a subfolder in the left tree, then click <em>Create Test Case</em>.</li>
          <li>Fill in: Description, Priority (Critical / High / Medium / Low), Preconditions, Steps, Expected Result, and Tags.</li>
        </ul>
        <h4>Importing Test Cases</h4>
        <ul>
          <li>Click <em>Import</em> in the action bar to upload an Excel (.xlsx) or TestRail XML file.</li>
          <li>XML imports automatically create subfolders matching the source structure.</li>
        </ul>
        <h4>Exporting Test Cases</h4>
        <ul>
          <li>Select one or more test cases using the checkboxes, then click <em>Export</em>.</li>
          <li>Downloads as an Excel file.</li>
        </ul>
        <h4>Bulk Delete</h4>
        <ul>
          <li>Select test cases and click <em>Delete Selected</em> to remove multiple at once.</li>
        </ul>
        <h4>Bulk Edit</h4>
        <ul>
          <li>Select 2 or more test cases and click <em>Bulk Edit</em> in the action bar.</li>
          <li>Change Priority and/or Tags (add or remove) across all selected items at once.</li>
          <li>Only fields you explicitly change get updated — blank fields are left as-is.</li>
        </ul>
        <h4>View Detail</h4>
        <ul>
          <li>Click any Test Case ID (e.g. <code>TC-12345</code>) in the table to open a read-only detail view.</li>
        </ul>

        <h4>Writing Standards</h4>
        <p>What to consider before writing a test case:</p>

        <h4>1. Coverage</h4>
        <ul>
          <li>Same checklist as Test Scenarios — Analyse the Story &amp; Figma, Impact Areas Coverage, Edge &amp; Negative Cases, Review to Improve Coverage — but broken down to concrete, executable steps instead of high-level intent.</li>
        </ul>

        <h4>2. Format</h4>
        <ul>
          <li><strong>Title/Description</strong>: specific and unique — describes the exact check, not the whole feature.</li>
          <li><strong>Pre-Condition</strong>: exact state needed before Step 1 (login role, existing data, feature flags, environment).</li>
          <li><strong>Priority</strong>: selected as per the nature of the test case and its impact — High / Medium / Low.</li>
          <li><strong>Steps</strong>: numbered, one action per step, written so any tester (not just the author) can execute it without guessing.</li>
          <li><strong>Expected Result</strong>: what should happen after each key step (or the final step) — stated as an observable, verifiable outcome, not an implementation detail.</li>
          <li><strong>Actual Result</strong>: upload the file to reference fulfilment of the Expected Result, only during execution in the Test Run execution cycle.</li>
        </ul>

        <h4>3. Design Alignment</h4>
        <ul>
          <li>Cross-check each step's expected UI/behaviour against the actual Figma spec (copy text, field validation messages, disabled/enabled states, error messages) — don't rely on memory of "how it usually looks".</li>
          <li>If the design has multiple states for the same screen, write separate test cases or explicit steps per state rather than assuming one covers both.</li>
        </ul>

        <h4>4. Execution</h4>
        <ul>
          <li>Execute test cases exactly as documented unless exploratory testing is intended.</li>
          <li>Follow the defined preconditions before execution.</li>
          <li>Validate both the UI and backend behaviour where applicable.</li>
          <li>Record the result immediately after each execution.</li>
          <li>Capture sufficient evidence for every failed test and whenever required by the project.</li>
          <li>If actual behaviour differs from the expected result, record it accurately without assumptions.</li>
          <li>Verify related functionality if the executed feature impacts other modules (regression awareness).</li>
          <li>Attach screenshots or a recording against each test case wherever it should cover the Expected Result.</li>
        </ul>

        <h4>Example</h4>
        <table className="help-formula-table">
          <tbody>
            <tr><td><strong>Title</strong></td><td>Bulk Delete button remains disabled with 0 test cases selected</td></tr>
            <tr><td><strong>Pre-Condition</strong></td><td>Logged in as Advertiser User; folder has ≥3 test cases</td></tr>
            <tr><td><strong>Priority</strong></td><td>High</td></tr>
            <tr><td><strong>Steps</strong></td><td>1. Navigate to Test Cases folder.<br/>2. Do not select any checkboxes.<br/>3. Observe the action bar.</td></tr>
            <tr><td><strong>Expected Result</strong></td><td>"Bulk Delete" button is visible but disabled (greyed out, not clickable).</td></tr>
            <tr><td><strong>Tags</strong></td><td>Functional, UI</td></tr>
          </tbody>
        </table>

        <div className="help-tip">
          <span className="help-tip-icon">{Icons.sliders}</span>
          <span>Use the <strong>Columns</strong> button to show/hide columns like Preconditions, Steps, Expected Result, and Created By.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'test-scenarios',
    icon: Icons.target,
    title: 'Test Scenarios',
    category: 'Core Workflows',
    content: (
      <div>
        <p>Test Scenarios are higher-level descriptions of what needs to be tested, often covering end-to-end flows. They are managed independently from Test Cases.</p>
        <h4>Creating Scenarios</h4>
        <ul>
          <li>Navigate to <strong>Test Scenarios</strong> in the sidebar.</li>
          <li>Select a folder, then click <em>Create Scenario</em>.</li>
          <li>Fill in: Title, Priority (Critical / High / Medium / Low), Classification Tags, and whether it's AI Generated (Yes/No).</li>
          <li>Classification Tags are optional. If AI Generated is set to <strong>Yes</strong>, the scenario is labelled "AI Generated". Otherwise it is automatically labelled "Manual" — no need to set it manually.</li>
          <li>Regression and AI Generated default to <strong>No</strong> but must be explicitly confirmed.</li>
        </ul>
        <h4>AI Generation</h4>
        <ul>
          <li>Click <em>Generate with AI</em> to automatically generate scenarios from a description using AI.</li>
          <li>Review the generated scenarios, select the ones you want, and save them to the folder.</li>
        </ul>
        <h4>Scenario IDs</h4>
        <ul>
          <li>Each scenario gets a unique ID (e.g. <code>SC-12345</code>) automatically assigned.</li>
          <li>Click the ID in the table to open a read-only detail view.</li>
        </ul>
        <h4>Bulk Edit</h4>
        <ul>
          <li>Select 2 or more scenarios and click <em>Bulk Edit</em> in the action bar.</li>
          <li>Change Priority, Environment, and/or Tags across all selected items at once.</li>
        </ul>
        <h4>Import / Export</h4>
        <ul>
          <li>Import scenarios from Excel using the <em>Import Excel</em> button.</li>
          <li>Select scenarios and click <em>Export</em> to download as Excel.</li>
        </ul>

        <h4>Writing Standards</h4>
        <p>What to consider before writing a scenario:</p>

        <h4>1. Analyse the Story &amp; Figma</h4>
        <ul>
          <li>Read the full user story/ticket (acceptance criteria, linked comments, dev notes) before writing — don't write from the title alone.</li>
          <li>Go through every Figma frame linked to the story: all screen states (default, loading, empty, error, success), all breakpoints/variants shown, and any inline design annotations.</li>
          <li>Note anything the story doesn't cover but the design implies (e.g. a disabled button state, a tooltip) — flag it as a scenario even if untested elsewhere.</li>
        </ul>

        <h4>2. Impact Areas Coverage</h4>
        <ul>
          <li>List every module/flow this change touches — directly (the feature itself) and indirectly.</li>
          <li>Check role/permission/user impact — does behaviour differ for Advertiser User vs Workspace User?</li>
          <li>Identify regression risk areas: existing flows that could break because of this change, even if not explicitly mentioned in the story.</li>
        </ul>

        <h4>3. Edge &amp; Negative Cases</h4>
        <ul>
          <li>Boundary values when required (min/max length, zero, negative numbers, max file size).</li>
          <li>Cover all validation while performing negative scenarios — invalid/malformed input, empty submissions, duplicate entries.</li>
          <li>Session/auth edge cases: expired token, mid-action logout, insufficient permissions.</li>
          <li>Network/failure conditions: API timeout, 4xx/5xx responses, partial data load.</li>
        </ul>

        <h4>4. Review to Improve Coverage</h4>
        <p>Peer Review is a critical quality gate that helps ensure Test Scenarios are complete, accurate, and aligned with business requirements before execution begins. It encourages knowledge sharing, reduces defects caused by missed validations, and improves overall test quality.</p>
        <ul>
          <li><strong>Improves Requirement Coverage</strong> — Helps identify missing functional, regression, edge, or negative scenarios that may have been overlooked by the author.</li>
          <li><strong>Validates Business Understanding</strong> — Confirms that scenarios accurately reflect the intended business behavior, acceptance criteria, and design specifications.</li>
          <li><strong>Ensures Consistency &amp; Standards</strong> — Verifies that Test Scenarios follow AssureX writing standards, naming conventions, priorities, and tagging guidelines.</li>
          <li><strong>Strengthens Collaboration &amp; Knowledge Sharing</strong> — Encourages discussion between team members, promoting shared understanding of the feature and improving overall QA expertise.</li>
          <li><strong>Reduces Production Risks</strong> — A second level of review increases confidence that critical business flows, integrations, permissions, and regression areas have been adequately covered.</li>
          <li><strong>Maintains Quality</strong> — A formal review and approval process provides confidence that the test design has been validated and is ready for execution.</li>
        </ul>

        <h4>5. Writing Format &amp; Examples</h4>
        <ul>
          <li>
            <strong>Title</strong>: action + outcome, not just a feature name.
            <ul>
              <li>❌ <code>Verify login</code></li>
              <li>✅ <code>Verify user is redirected to dashboard after successful Google OAuth login</code></li>
            </ul>
          </li>
          <li><strong>Structure</strong>: one clear end-to-end intent per scenario — if you're using "and", check whether it's really two scenarios.</li>
          <li><strong>Tags</strong>: apply relevant classification tags (Regression, Smoke, UI, Functional, etc.) so it's discoverable later.</li>
        </ul>

        <div className="help-tip">
          <span className="help-tip-icon">{Icons.layers}</span>
          <span>Scenarios and Test Cases are separate modules with their own folder trees. A scenario folder is independent of a test case folder.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'test-runs',
    icon: Icons.play,
    title: 'Test Runs',
    category: 'Core Workflows',
    content: (
      <div>
        <p>Test Runs let you execute a set of test cases or scenarios and record pass/fail results with evidence.</p>
        <h4>Creating a Test Run</h4>
        <ul>
          <li>Click <em>Create Test Run</em> from the action bar in Test Cases or Test Scenarios.</li>
          <li>Or navigate to <strong>Test Runs</strong> → <em>Create Test Run</em>.</li>
          <li>Select the source folder, run type (Test Case or Scenario), environment, and run name.</li>
        </ul>
        <h4>Executing a Test Run</h4>
        <ul>
          <li>Open a test run to see all items listed.</li>
          <li>For each item, set the result: <strong>Pass</strong>, <strong>Fail</strong>, or <strong>Blocked</strong>.</li>
          <li>Upload evidence screenshots or files for failed items.</li>
          <li>Add notes or comments per test item.</li>
        </ul>
        <h4>Evidence Storage</h4>
        <ul>
          <li>Evidence files are stored in S3 under a structured path: <code>Test Execution / Environment / Run Type / Project / Subfolder / Run Name / filename</code>.</li>
          <li>Files are named using the test case/scenario ID with a sequence number (e.g. <code>TC-12345_1.jpg</code>).</li>
        </ul>
        <div className="help-tip">
          <span className="help-tip-icon">{Icons.grid}</span>
          <span>Test run results are tracked over time — you can view historical runs to monitor quality trends.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'folders',
    icon: Icons.layers,
    title: 'Folder Management',
    category: 'Core Workflows',
    content: (
      <div>
        <p>Both Test Cases and Test Scenarios use a hierarchical folder tree to organize content.</p>
        <h4>Creating Subfolders</h4>
        <ul>
          <li>Select a folder in the tree, then click <em>Create Subfolder</em>.</li>
          <li>Subfolders can be nested to any depth.</li>
        </ul>
        <h4>Renaming Folders</h4>
        <ul>
          <li>Right-click a folder in the tree to open the context menu, then select <em>Rename</em>.</li>
        </ul>
        <h4>Moving Folders</h4>
        <ul>
          <li>Right-click a folder and select <em>Move to...</em> to move it under a different parent folder.</li>
          <li>A picker shows all valid destination folders — you cannot move a folder into itself or its own children.</li>
          <li>Moving a folder updates the S3 Test Data XML paths automatically.</li>
        </ul>
        <h4>Deleting Folders</h4>
        <ul>
          <li>Deleting a folder removes all its subfolders and test content. This action cannot be undone.</li>
        </ul>
        <h4>Collapsing the Sidebar</h4>
        <ul>
          <li>Click the toggle button on the sidebar edge to collapse it and gain more screen space.</li>
        </ul>
        <h4>Share Link</h4>
        <ul>
          <li>Click <em>Share Link</em> in the action bar to generate a human-readable URL for a folder.</li>
          <li>The link includes the full folder path (e.g. <code>/test-scenarios/Campaigns/CB-34?folder_id=...</code>).</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'peer-review',
    icon: Icons.eyeCheck,
    title: 'Peer Review Process',
    category: 'Quality & Collaboration',
    content: (
      <div>
        <p>AssureX includes a mandatory peer review workflow for Test Scenario and Test Case folders before they can be used in Test Runs.</p>
        <h4>Review States</h4>
        <ul>
          <li><strong>Draft</strong> — Default state when a folder is created.</li>
          <li><strong>In Review</strong> — Submitted for review, awaiting reviewer action.</li>
          <li><strong>Approved</strong> — Reviewed and approved. Test Runs can now be created from this folder.</li>
          <li><strong>Changes Requested</strong> — Reviewer has requested changes. Submitter must fix and re-submit.</li>
        </ul>
        <h4>Submitting for Review</h4>
        <ul>
          <li>Right-click a folder in the tree and select <em>Submit for Review</em>.</li>
          <li>Enter the reviewer's email address and submit.</li>
          <li>The reviewer and all Admins receive an email notification with a direct link to the folder.</li>
        </ul>
        <h4>Reviewing a Folder</h4>
        <ul>
          <li>The assigned reviewer sees the folder in their <strong>Notifications</strong> panel under <em>In Review</em>.</li>
          <li>Right-click the folder and select <em>Review</em> to open the review panel.</li>
          <li>Complete the verification checklist, add a comment, then click <strong>Approve</strong> or <strong>Request Changes</strong>.</li>
          <li>The submitter and all Admins receive an email with the decision and any comments.</li>
        </ul>
        <h4>After Changes Requested</h4>
        <ul>
          <li>The folder appears in the submitter's <em>In Review</em> notification tab with a red "Changes Requested" badge.</li>
          <li>Fix the scenarios/test cases, then right-click the folder and <em>Submit for Review</em> again.</li>
        </ul>
        <h4>Test Run Gate</h4>
        <ul>
          <li>Only <strong>Approved</strong> folders appear in the Test Run creation dropdowns.</li>
          <li>The <em>Create Test Run</em> button is disabled for non-approved folders.</li>
        </ul>
        <div className="help-tip">
          <span className="help-tip-icon">{Icons.eyeCheck}</span>
          <span>The review status badge is visible in the breadcrumb bar when a folder is selected — green = Approved, blue = In Review, red = Changes Requested.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'notifications',
    icon: Icons.bell,
    title: 'Notifications',
    category: 'Quality & Collaboration',
    content: (
      <div>
        <p>The notification bell in the header shows your pending and completed review items.</p>
        <h4>In Review Tab</h4>
        <ul>
          <li>Shows folders assigned to <strong>you as reviewer</strong> that are waiting for your action.</li>
          <li>Also shows folders you submitted that have <strong>Changes Requested</strong> — these need your attention.</li>
          <li>Click <em>Review →</em> to navigate directly to the folder.</li>
          <li>The badge count on the bell shows how many items need your attention.</li>
        </ul>
        <h4>Completed Tab</h4>
        <ul>
          <li>Shows folders you submitted or reviewed that have been <strong>Approved</strong>.</li>
          <li>Click <em>Open →</em> to navigate to the folder.</li>
        </ul>
        <div className="help-tip">
          <span className="help-tip-icon">{Icons.bell}</span>
          <span>Notifications auto-refresh every 60 seconds. Click the refresh button to update manually.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'admin',
    icon: Icons.lock,
    title: 'Admin Console',
    category: 'Administration',
    content: (
      <div>
        <p>The Admin Console is available to users with the <strong>Admin</strong> role.</p>
        <h4>User Management</h4>
        <ul>
          <li>View all registered users in the system.</li>
          <li>Change user roles (Admin, QA Lead, Tester, Viewer).</li>
          <li>Deactivate or remove users.</li>
        </ul>
        <h4>Role Permissions</h4>
        <ul>
          <li><strong>Admin</strong> — Full access including user management and all CRUD operations.</li>
          <li><strong>QA Lead</strong> — Can create/edit/delete projects, folders, test cases, scenarios, and runs.</li>
          <li><strong>Tester</strong> — Can create and execute test runs; limited edit access.</li>
          <li><strong>Viewer</strong> — Read-only access to all content.</li>
        </ul>
        <div className="help-tip">
          <span className="help-tip-icon">{Icons.lock}</span>
          <span>Only Admins can access the Admin Console. The link appears in the sidebar only when logged in as Admin.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'columns',
    icon: Icons.sliders,
    title: 'Column Selector',
    category: 'Administration',
    content: (
      <div>
        <p>Both Test Cases and Test Scenarios tables support customizable columns.</p>
        <ul>
          <li>Click the <strong>Columns</strong> button in the controls bar to open the column selector.</li>
          <li>Toggle any column on or off. Required columns (ID, Title, Actions) cannot be hidden.</li>
          <li>Your column preferences are saved in the browser and persist across sessions.</li>
        </ul>
        <h4>Available Columns</h4>
        <ul>
          <li><strong>ID</strong> — Unique test case/scenario identifier</li>
          <li><strong>Title / Description</strong> — The test name</li>
          <li><strong>Priority</strong> — Critical, High, Medium, or Low</li>
          <li><strong>Tags</strong> — Labels like Regression, Smoke, Sanity</li>
          <li><strong>Preconditions</strong> — Setup requirements (Test Cases only)</li>
          <li><strong>Steps</strong> — Step-by-step instructions (Test Cases only)</li>
          <li><strong>Expected Result</strong> — What should happen</li>
          <li><strong>Created Date</strong> — When the item was created</li>
          <li><strong>Created By</strong> — Who created it</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'search',
    icon: Icons.search,
    title: 'Search & Filter',
    category: 'Administration',
    content: (
      <div>
        <p>AssureX provides search at multiple levels:</p>
        <h4>Global Search (Header)</h4>
        <ul>
          <li>The search bar in the top header is for navigating across projects, tests, and documentation.</li>
        </ul>
        <h4>Table Search</h4>
        <ul>
          <li>Each Test Cases and Test Scenarios table has its own search bar.</li>
          <li>Searches across ID, title/description, expected result, and tags simultaneously.</li>
          <li>Results update in real time as you type.</li>
          <li>A <em>Filtered: N</em> badge appears showing how many results match.</li>
        </ul>
        <h4>Project Search</h4>
        <ul>
          <li>On the Projects screen, use the search bar to filter project cards by name or description.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'whats-new',
    icon: Icons.zap,
    title: "What's New",
    category: 'Reference',
    content: (
      <div>
        <p>Recent updates and improvements to AssureX — keep your team informed of new capabilities.</p>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">August 2026</div>
          <h4>Dashboard Redesign — Quality Health &amp; AI Productivity</h4>
          <ul>
            <li>The Dashboard now leads with a <strong>Quality Health</strong> status (Healthy / Attention / At Risk / Critical) instead of raw counts, so anyone can tell in seconds whether things are okay.</li>
            <li>New <strong>Test Coverage</strong> and <strong>Critical Risks</strong> KPIs, each with a trend badge vs. the prior period.</li>
            <li>New <strong>AI Productivity</strong> panel showing an <em>estimated</em> time-saved figure — tunable by an Admin under Admin Console → Dashboard Settings.</li>
            <li>New <strong>Project Quality Overview</strong>, <strong>Executive Insights</strong>, and <strong>Attention Required</strong> panels — all generated from real thresholds, not fixed text.</li>
            <li>Fixed a bug where AI-generated <em>test cases</em> (not just scenarios) were never counted toward AI adoption metrics.</li>
            <li>See the <strong>Dashboard Formulas</strong> section below for the exact calculation behind every metric.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Analytics Dashboard — Historical Activity Report</h4>
          <ul>
            <li><strong>Overall View</strong> now shows 6 columns: TC, TC Executed, TS, TS Executed, Runs Created, Runs Completed — giving upper management full execution coverage visibility.</li>
            <li><strong>AI vs Manual View</strong> now shows a consolidated <em>Overall</em> summary row at the top of each period group, with total counts and recalculated percentages across all users.</li>
            <li>Fixed a bug where the Overall view showed "No activity data" on page refresh.</li>
            <li>Dashboard KPI — <strong>Total Projects</strong> now correctly counts distinct project names across both Test Case and Test Scenario folder trees.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Peer Review — Admin Override</h4>
          <ul>
            <li>Admins can now <strong>Approve or Request Changes</strong> on any folder that is "In Review", even if they are not the assigned reviewer.</li>
            <li>This unblocks teams when the assigned reviewer is unavailable.</li>
            <li>The review modal shows a yellow <em>Admin Override</em> notice when an Admin is acting on behalf of the assigned reviewer.</li>
            <li>The verification checklist is skipped for Admin overrides.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Defect Reference — Jira Linkification</h4>
          <ul>
            <li>In the <strong>Execute Test Case</strong> modal, typing a Jira ticket ID (e.g. <code>PLANNER-545</code>) in the Defect Reference field now shows a live clickable preview link.</li>
            <li>In the <strong>Test Run Detail</strong> table, the DEFECT column renders ticket IDs as clickable links that open directly in Jira.</li>
            <li>Multiple ticket IDs in one field are all linkified.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Sync New — Test Run</h4>
          <ul>
            <li>The <strong>⟳ Sync New</strong> button on Test Run Detail now works correctly. It adds any scenarios or test cases created after the run was originally created.</li>
            <li>Only works on NOT_STARTED or IN_PROGRESS runs.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Import Tag Validation</h4>
          <ul>
            <li>Excel imports for Test Scenarios and Test Cases now validate tags against a whitelist.</li>
            <li>Only approved tags are accepted: <code>Smoke, Sanity, Integration, API, UI, Performance, Security, Functional, Regression, AI Generated</code>.</li>
            <li>Tags not in the whitelist are silently dropped — the import still succeeds.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Folder Jira Link in Breadcrumb</h4>
          <ul>
            <li>Test Scenario folder names containing Jira ticket IDs (e.g. <code>PLANNER-545</code>) are automatically rendered as clickable links in the breadcrumb.</li>
            <li>Clicking opens the ticket directly in Jira in a new tab.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Evidence Download for Completed Runs</h4>
          <ul>
            <li>Completed (immutable) test runs now show a download icon in the Actions column.</li>
            <li>Click it to download evidence files uploaded during execution.</li>
            <li>If multiple files exist, a picker modal lets you choose which file to download.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Cycle Label Management</h4>
          <ul>
            <li>Test Run cycle labels can now be renamed — all runs sharing the same label are updated together.</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 'dashboard-formulas',
    icon: Icons.grid,
    title: 'Dashboard Formulas',
    category: 'Reference',
    content: (
      <div>
        <p>
          Every metric on the Dashboard (<code>/</code>) is calculated live from real execution data — nothing is
          hardcoded or simulated except where explicitly labeled <strong>Estimated</strong>. Use this page as the
          reference when explaining a number to management or double-checking what it actually means.
        </p>
        <p>
          Unless noted otherwise, "the selected period" means whatever date range is chosen in the Dashboard's top-right
          dropdown (Today / Last 7 Days / Last 30 Days / Last 90 Days / Month to Date / Quarter to Date). Only executions
          with a real status — <code>PASS</code>, <code>FAIL</code>, or <code>BLOCKED</code> — count toward any rate;
          <code>NOT_RUN</code> rows are always excluded.
        </p>

        <h4>Quality Health (top banner)</h4>
        <p>
          A single status — <strong>Healthy</strong>, <strong>Attention</strong>, <strong>At Risk</strong>, or
          <strong> Critical</strong> — computed from three inputs for the selected period: Test Pass Rate, Critical
          Risks, and Blocked %. Whichever input is worst decides the status:
        </p>
        <table className="help-formula-table">
          <thead><tr><th>Status</th><th>Triggered when (worst condition wins)</th></tr></thead>
          <tbody>
            <tr><td><strong>Healthy</strong></td><td>Pass rate ≥ 95% <em>and</em> 0 critical failures <em>and</em> blocked &lt; 5%</td></tr>
            <tr><td><strong>Attention</strong></td><td>Pass rate 85–95%, <em>or</em> 1–2 critical failures, <em>or</em> blocked 5–15%</td></tr>
            <tr><td><strong>At Risk</strong></td><td>Pass rate 70–85%, <em>or</em> 3–5 critical failures, <em>or</em> blocked &gt; 15%</td></tr>
            <tr><td><strong>Critical</strong></td><td>Pass rate &lt; 70%, <em>or</em> more than 5 critical failures</td></tr>
          </tbody>
        </table>
        <p>If there are zero executions in the selected period, the status shows as <strong>Unknown</strong> rather than a misleading "Healthy" — there simply isn't enough data yet to judge.</p>

        <h4>Primary KPIs</h4>
        <table className="help-formula-table">
          <thead><tr><th>Metric</th><th>What it means</th><th>Formula</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>Test Pass Rate</strong></td>
              <td>Of everything actually executed in the period, what % passed.</td>
              <td><code>ROUND(pass_count / (pass + fail + blocked) × 100, 1)</code></td>
            </tr>
            <tr>
              <td><strong>Test Coverage</strong></td>
              <td>What % of your Test Scenarios have been run at least once, <em>ever</em> (this is all-time, not limited to the selected period — a scenario doesn't lose "coverage" just because it wasn't re-run this month).</td>
              <td><code>COUNT(DISTINCT scenarios with ≥1 execution WHERE status ≠ NOT_RUN) / COUNT(all latest scenarios) × 100</code></td>
            </tr>
            <tr>
              <td><strong>Critical Risks</strong></td>
              <td>How many <em>Critical</em>-priority scenarios or test cases failed in the selected period. This is the number that should worry you most — it ignores Medium/Low priority failures entirely.</td>
              <td><code>COUNT(executions WHERE status = FAIL AND linked scenario/test case priority = 'Critical')</code></td>
            </tr>
            <tr>
              <td><strong>AI Time Saved</strong></td>
              <td>An <strong>estimate</strong> of QA hours saved by using AI generation instead of writing scenarios/test cases by hand. See "AI Productivity" below for the exact formula — this tile is never a measured/actual number.</td>
              <td>See AI Productivity section</td>
            </tr>
          </tbody>
        </table>
        <p>
          Each KPI tile also shows a small trend badge (▲/▼) comparing the current period to the immediately
          preceding period of the <em>same length</em> — e.g. for "Last 30 Days," it compares against the 30 days
          before that.
        </p>
        <p>Below the 4 primary tiles, a lighter secondary strip still shows the raw totals for context:</p>
        <table className="help-formula-table">
          <thead><tr><th>Metric</th><th>Formula</th><th>Notes</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>Test Cases / Scenarios</strong></td>
              <td><code>COUNT(test_cases)</code> / <code>COUNT(test_scenarios WHERE is_latest = true)</code></td>
              <td>All-time totals, not limited to the selected period.</td>
            </tr>
            <tr>
              <td><strong>Test Runs</strong></td>
              <td><code>COUNT(test_runs WHERE created_at ≥ period start)</code></td>
              <td>Scoped to the selected period.</td>
            </tr>
            <tr>
              <td><strong>Projects</strong></td>
              <td><code>COUNT(DISTINCT root folder names)</code></td>
              <td>Root-level folder names act as a Project stand-in — AssureX doesn't have a dedicated Project entity yet.</td>
            </tr>
          </tbody>
        </table>

        <h4>AI Productivity</h4>
        <p>
          There is no log of how long anyone actually spent writing a scenario or reviewing an AI-generated one — so
          this whole section is an <strong>estimate</strong>, clearly labeled as such, built from admin-configurable
          assumptions (Admin Console → Dashboard Settings) rather than hardcoded numbers.
        </p>
        <table className="help-formula-table">
          <thead><tr><th>Metric</th><th>Formula</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>AI-Generated Count</strong></td>
              <td><code>COUNT(scenarios tagged "AI Generated" created in period) + COUNT(test cases tagged "AI Generated" created in period)</code></td>
            </tr>
            <tr>
              <td><strong>Adoption %</strong></td>
              <td><code>ai_generated_count / (all scenarios + test cases created in period) × 100</code></td>
            </tr>
            <tr>
              <td><strong>Estimated Hours Saved</strong></td>
              <td><code>((AI scenarios × avg. manual scenario time) + (AI test cases × avg. manual test case time) − (AI count × avg. AI review time)) ÷ 60</code></td>
            </tr>
            <tr>
              <td><strong>Equivalent Working Days</strong></td>
              <td><code>estimated_hours_saved ÷ 8</code></td>
            </tr>
          </tbody>
        </table>
        <div className="help-tip">
          ⚙️ The three time constants (default: 25 min/scenario, 15 min/test case, 5 min AI-review) are tunable by an
          Admin under <strong>Admin Console → Dashboard Settings</strong> — adjust them to match your team's real pace
          and every dashboard relying on this estimate updates immediately.
        </div>

        <h4>Epic Quality Overview</h4>
        <p>
          This table groups folders by their tagged <strong>Epic ID</strong> (set via the "Set Epic ID" action on a
          folder, or seen in the folder search panel's Epic search) rather than by project — a single Epic can span
          story folders that live under different projects. Epics are listed <strong>most-recently tagged first</strong>:
          since there's no separate "Epic" record with its own creation date, the most recent <code>updated_at</code>
          among a folder tagged with that Epic ID is used as the "latest added" signal. Click a row to expand and see
          the individual story folders tagged under that Epic.
        </p>
        <table className="help-formula-table">
          <thead><tr><th>Column</th><th>Formula</th></tr></thead>
          <tbody>
            <tr><td><strong>Quality</strong></td><td>Same Healthy/Attention/At Risk/Critical rule as the top banner, computed using only that Epic's executions (all-time, not period-limited).</td></tr>
            <tr><td><strong>Pass %</strong></td><td><code>pass / (pass + fail + blocked)</code> across every folder tagged with that Epic ID.</td></tr>
            <tr><td><strong>Coverage</strong></td><td>Same "executed at least once" formula as Test Coverage above, scoped to that Epic's scenarios.</td></tr>
            <tr><td><strong>Failed</strong></td><td>Count of FAIL executions, all-time, within that Epic.</td></tr>
            <tr><td><strong>Trend</strong></td><td>Compares that Epic's pass rate in the last 30 days vs. the 30 days before — ▲ if it improved by more than 1 point, ▼ if it dropped by more than 1 point, otherwise flat.</td></tr>
          </tbody>
        </table>
        <div className="help-tip">
          💡 Only folders that have an Epic ID tagged on them appear here. If this table is empty or missing an Epic
          you expect, tag the relevant folder(s) with that Epic ID first.
        </div>

        <h4>Executive Insights &amp; Attention Required</h4>
        <p>
          Neither of these panels contains fixed/canned text — every line is generated only when a real threshold is
          crossed, using the same period-over-period comparison as the KPI trend badges:
        </p>
        <ul>
          <li><strong>Executive Insights</strong> fires when: pass rate moves ≥ 2 points, AI adoption moves ≥ 5 points, or execution volume moves ≥ 10% vs. the prior period. If nothing crossed those thresholds, it simply says "No notable changes this period."</li>
          <li><strong>Attention Required</strong> lists, in order: any Critical Risks &gt; 0, blocked rate &gt; 15%, pass rate &lt; 70%, and any test run that's been "In Progress" for more than 14 days. If none of those are true, it shows a single reassuring line instead of an empty box.</li>
        </ul>

        <h4>Quality &amp; Execution Trend (chart)</h4>
        <p>
          Plots daily Pass / Fail / Blocked counts plus a dashed total-Volume line, for the range selected on the
          chart itself (7 / 30 / 90 days — independent of the page-level date filter above). The small "vs. prior
          period" figure next to the range toggle compares total execution volume to the equal-length period before it.
        </p>

        <h4>Testing Activity &amp; QA Productivity</h4>
        <p>These reuse the same period-bucketed activity data as before, just condensed to the latest period + a trend arrow instead of a full historical table (the full table has moved to a future Reports view):</p>
        <table className="help-formula-table">
          <thead><tr><th>Metric</th><th>Formula</th></tr></thead>
          <tbody>
            <tr><td><strong>Test Cases / Scenarios Created</strong></td><td><code>COUNT(created WHERE created_at falls in the latest month)</code>, compared against the previous month</td></tr>
            <tr><td><strong>Test Cases / Scenarios Executed</strong></td><td><code>COUNT(executions WHERE status IN (PASS,FAIL,BLOCKED) AND executed_at falls in the latest month)</code></td></tr>
            <tr><td><strong>Items Created / Day</strong></td><td><code>(test cases + scenarios created this month) ÷ 30</code></td></tr>
            <tr><td><strong>AI-Assisted %</strong></td><td>Same Adoption % as the AI Productivity section, for the last 30 days.</td></tr>
          </tbody>
        </table>
        <div className="help-tip">
          💡 The underlying <strong>AI vs. Manual</strong> breakdown now correctly counts AI-generated <em>test cases</em>
          as well as scenarios — a previous version of this report only checked scenarios, silently under-counting AI
          adoption whenever teams generated test cases with AI.
        </div>

        <h4>Recent Test Runs &amp; Test Run Pass Rate</h4>
        <table className="help-formula-table">
          <thead><tr><th>Metric</th><th>Formula</th></tr></thead>
          <tbody>
            <tr>
              <td><strong>Pass Rate %</strong></td>
              <td><code>ROUND(pass_count / (pass + fail + blocked) × 100, 1)</code></td>
            </tr>
            <tr>
              <td><strong>Progress %</strong></td>
              <td><code>ROUND((pass + fail + blocked) / total_cases × 100)</code></td>
            </tr>
          </tbody>
        </table>

        <div className="help-tip">
          💡 "NOT_RUN" executions are excluded from every percentage calculation on this page — only executed items (PASS, FAIL, BLOCKED) count toward rates.
        </div>
      </div>
    ),
  },
  {
    id: 'bug-format',
    icon: Icons.bug,
    title: 'Bug Format',
    category: 'Reference',
    content: (
      <div>
        <p>Follow this structure when logging a defect so developers and QA can triage, reproduce, and resolve it without back-and-forth.</p>

        <h4>1. Bug Title</h4>
        <p>Provide a clear, concise, and descriptive title that summarizes the issue and the affected functionality.</p>

        <h4>2. Description</h4>
        <p>Include all relevant information required to understand, reproduce, and investigate the defect.</p>
        <ul>
          <li><strong>2.1 Environment</strong> — Specify the environment, application version/build, browser, operating system, and device (if applicable).</li>
          <li><strong>2.2 Preconditions</strong> — List any setup, configuration, or prerequisites required before reproducing the issue.</li>
          <li><strong>2.3 Steps to Reproduce</strong> — Provide clear, sequential steps that consistently reproduce the defect.</li>
          <li><strong>2.4 Actual Result</strong> — Describe the behavior observed after performing the reproduction steps.</li>
          <li><strong>2.5 Expected Result</strong> — Describe the expected system behavior according to the requirement or design.</li>
          <li><strong>2.6 Evidence</strong> — Attach supporting artifacts such as screenshots, screen recordings, logs, or HAR files to help validate the issue.</li>
          <li><strong>2.7 cURL/API Details</strong> — Include the API endpoint, request/response details, status code, and cURL command (if the issue is API-related).</li>
          <li><strong>2.8 Datadog Trace</strong> — Provide the Datadog Trace ID or trace link to assist developers in identifying the backend root cause.</li>
        </ul>

        <h4>3. Labels</h4>
        <p>Add relevant labels to categorize the bug and improve searchability and reporting.</p>

        <h4>4. Assignee</h4>
        <p>Assign the bug to the appropriate developer or team responsible for resolving the issue.</p>

        <h4>5. Priority</h4>
        <p>Specify the urgency of fixing the defect based on its business impact.</p>

        <h4>6. Severity</h4>
        <p>Indicate the technical impact of the defect on the application's functionality.</p>

        <h4>7. QA Assignee</h4>
        <p>Assign the QA engineer responsible for validating the fix and performing regression testing.</p>

        <h4>8. Fix Version</h4>
        <p>Specify the target application version or release in which the defect is planned to be fixed.</p>

        <h4>9. Primary POD</h4>
        <p>Specify the primary Product-Oriented Delivery (POD) or team that owns the affected functionality and is responsible for resolving the defect.</p>

        <h4>10. Impacted POD</h4>
        <p>List any additional POD(s) or teams that may be impacted by the defect or whose functionality could be affected by the fix. This helps ensure proper visibility, coordination, and regression coverage across teams.</p>

        <div className="help-tip">
          <span className="help-tip-icon">{Icons.bug}</span>
          <span>A well-structured bug report speeds up triage and root-cause analysis — always attach evidence, and for API issues include the cURL command and Datadog trace so developers don't have to ask for it separately.</span>
        </div>
      </div>
    ),
  },
  {
    id: 'whats-new',
    icon: Icons.star,
    title: "What's New",
    category: 'Reference',
    content: (
      <div>
        <p>Recent updates and improvements to AssureX — keep your team informed of new capabilities.</p>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Analytics Dashboard — Historical Activity Report</h4>
          <ul>
            <li><strong>Overall View</strong> now shows 6 columns: TC, TC Executed, TS, TS Executed, Runs Created, Runs Completed — giving upper management full execution coverage visibility.</li>
            <li><strong>AI vs Manual View</strong> now shows a consolidated <em>Overall</em> summary row at the top of each period group, with total counts and recalculated percentages across all users.</li>
            <li>Fixed a bug where the Overall view showed "No activity data" on page refresh.</li>
            <li>Dashboard KPI — <strong>Total Projects</strong> now correctly counts distinct project names across both Test Case and Test Scenario folder trees.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Peer Review — Admin Override</h4>
          <ul>
            <li>Admins can now <strong>Approve or Request Changes</strong> on any folder that is "In Review", even if they are not the assigned reviewer.</li>
            <li>This unblocks teams when the assigned reviewer is unavailable.</li>
            <li>The review modal shows a yellow <em>Admin Override</em> notice when an Admin is acting on behalf of the assigned reviewer.</li>
            <li>The verification checklist is skipped for Admin overrides.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Defect Reference — Jira Linkification</h4>
          <ul>
            <li>In the <strong>Execute Test Case</strong> modal, typing a Jira ticket ID (e.g. <code>PLANNER-545</code>) in the Defect Reference field now shows a live clickable preview link.</li>
            <li>In the <strong>Test Run Detail</strong> table, the DEFECT column renders ticket IDs as clickable links that open directly in Jira.</li>
            <li>Multiple ticket IDs in one field are all linkified.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Sync New — Test Run</h4>
          <ul>
            <li>The <strong>Sync New</strong> button on Test Run Detail now works correctly. It adds any scenarios or test cases created after the run was originally created.</li>
            <li>Only works on NOT_STARTED or IN_PROGRESS runs.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">June 2026</div>
          <h4>Import Tag Validation</h4>
          <ul>
            <li>Excel imports for Test Scenarios and Test Cases now validate tags against a whitelist.</li>
            <li>Only approved tags are accepted: <code>Smoke, Sanity, Integration, API, UI, Performance, Security, Functional, Regression, AI Generated</code>.</li>
            <li>Tags not in the whitelist are silently dropped — the import still succeeds.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Folder Jira Link in Breadcrumb</h4>
          <ul>
            <li>Test Scenario folder names containing Jira ticket IDs (e.g. <code>PLANNER-545</code>) are automatically rendered as clickable links in the breadcrumb.</li>
            <li>Clicking opens the ticket directly in Jira in a new tab.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Evidence Download for Completed Runs</h4>
          <ul>
            <li>Completed (immutable) test runs now show a download icon in the Actions column.</li>
            <li>Click it to download evidence files uploaded during execution.</li>
            <li>If multiple files exist, a picker modal lets you choose which file to download.</li>
          </ul>
        </div>

        <div className="help-changelog-entry">
          <div className="help-changelog-date">May 2026</div>
          <h4>Cycle Label Management</h4>
          <ul>
            <li>Test Run cycle labels can now be renamed — all runs sharing the same label are updated together.</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 'shortcuts',
    icon: Icons.zap,
    title: 'Tips & Shortcuts',
    category: 'Reference',
    content: (
      <div>
        <ul>
          <li>Press <kbd>Enter</kbd> in the tag input field to add a tag without clicking the button.</li>
          <li>Click a test case/scenario ID in the table to open its read-only detail view.</li>
          <li>Use the sidebar collapse button to maximize the content area.</li>
          <li>Select 2+ items and click <em>Bulk Edit</em> to update Priority and Tags across all at once.</li>
          <li>Right-click any folder in the tree to access Create Subfolder, Rename, Move, Submit for Review, and Delete.</li>
          <li>The <em>Share Link</em> feature generates a human-readable URL — recipients are taken directly to that folder.</li>
          <li>Column preferences are saved per browser — each team member can have their own view.</li>
          <li>Importing a TestRail XML file automatically recreates the folder hierarchy from the source.</li>
          <li>Evidence files uploaded during test runs are automatically organized in S3 by environment, run type, and project.</li>
          <li>Test Data (scenarios and test cases) is automatically synced to S3 as XML after every create, update, or delete.</li>
          <li>After logout, Google will always show the account picker — you can switch accounts on every login.</li>
        </ul>
      </div>
    ),
  },
];

const CATEGORY_ORDER = ['Getting Started', 'Core Workflows', 'Quality & Collaboration', 'Administration', 'Reference'];

export const HelpCenterPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [query, setQuery] = useState('');

  const current = sections.find(s => s.id === activeSection) || sections[0];

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(s => s.title.toLowerCase().includes(q) || s.category.toLowerCase().includes(q));
  }, [query]);

  const groups = useMemo(() => {
    const byCategory = new Map<string, Section[]>();
    for (const s of filteredSections) {
      const list = byCategory.get(s.category) || [];
      list.push(s);
      byCategory.set(s.category, list);
    }
    return CATEGORY_ORDER
      .map(category => ({ category, items: byCategory.get(category) || [] }))
      .filter(group => group.items.length > 0);
  }, [filteredSections]);

  return (
    <div className="help-center-page">
      <aside className="help-sidebar">
        <div className="help-sidebar-header">
          <div className="help-sidebar-brand">
            <span className="help-sidebar-brand-icon">{Icons.book}</span>
            <div>
              <h2>Help Center</h2>
              <p>AssureX User Guide</p>
            </div>
          </div>
          <div className="help-search">
            <span className="help-search-icon">{Icons.search}</span>
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search help topics..."
              aria-label="Search help topics"
            />
          </div>
        </div>

        <nav className="help-nav">
          {groups.map(group => (
            <div className="help-nav-group" key={group.category}>
              <div className="help-nav-group-label">{group.category}</div>
              {group.items.map(s => (
                <button
                  key={s.id}
                  className={`help-nav-item${activeSection === s.id ? ' active' : ''}`}
                  onClick={() => setActiveSection(s.id)}
                >
                  <span className="help-nav-icon">{s.icon}</span>
                  <span className="help-nav-label">{s.title}</span>
                </button>
              ))}
            </div>
          ))}
          {groups.length === 0 && (
            <div className="help-nav-empty">No topics match &ldquo;{query}&rdquo;</div>
          )}
        </nav>
      </aside>

      <div className="help-content">
        <div className="help-content-header">
          <span className="help-content-icon">{current.icon}</span>
          <div className="help-content-heading">
            <div className="help-content-breadcrumb">{current.category}</div>
            <h1>{current.title}</h1>
          </div>
        </div>
        <div className="help-content-body">
          {current.content}
        </div>
      </div>
    </div>
  );
};
